import * as React from 'react';
import { ReactNode, useEffect, useState } from 'react';
import '../node_modules/primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import { pickLocale } from './Intl/utils';
import { MathJaxContext } from 'better-react-mathjax';
import { addLocale, LocaleOptions, PrimeReactProvider } from 'primereact/api';
import { StateProvider } from './util/providers/StateProvider';
import { ToastProvider } from './util/providers/ToastProvider';
import { calenderLocales } from './Settings/calender-locales';
import { ScrollTop } from 'primereact/scrolltop';
import { BaseLayout } from './pages/BaseLayout';
import { Routing } from './routing/Routing';
import { AuthProvider } from './Auth/components/AuthProvider';
import { mathjaxConfig } from './Settings/mathjax-config';


export const localeKey: string = 'locale';

const getPreferredLocales = (): string[] => {
  const preferred: string[] = [...(navigator.languages || [navigator.language])];
  const storedLocale: string = window.localStorage.getItem(localeKey);
  if (storedLocale !== null) {
    preferred.unshift(storedLocale);
  }
  return preferred;
};

const selectLocale = (): string => {
  const preferred: string[] = getPreferredLocales();
  return pickLocale(preferred);
};

// Add locales for CalendarComponent of PrimeReact
calenderLocales.forEach((locale: [string, LocaleOptions]): void => {
  addLocale(...locale);
});


export const App: React.FC = (): ReactNode => {
  const [locale, setLocale] = useState<string>(selectLocale());

  useEffect((): () => void => {
    const handleLocaleChange = (): void => {
      setLocale(selectLocale());
    };

    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key !== localeKey) {
        return;
      }
      handleLocaleChange();
    };
    window.addEventListener('languagechange', handleLocaleChange);
    window.addEventListener('applanguagechange', handleLocaleChange);
    window.addEventListener('storage', handleStorageChange);


    return (): void => {
      window.removeEventListener('languagechange', handleLocaleChange);
      window.removeEventListener('applanguagechange', handleLocaleChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <StateProvider>
      <PrimeReactProvider>
        <AuthProvider>
          <BaseLayout>
            <MathJaxContext src={'/libs/mathjax.js'} config={mathjaxConfig}>
              <ToastProvider>
                <React.Suspense fallback={<div>Loading...</div>}>
                  <Routing/>
                  <ScrollTop/>
                </React.Suspense>
              </ToastProvider>
            </MathJaxContext>
          </BaseLayout>
        </AuthProvider>
      </PrimeReactProvider>
    </StateProvider>
  );
};
